import * as React from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import { Button } from "@/shared/components/ui/button";
import { Calculator } from "lucide-react";

type CalcOp = "+" | "−" | "×" | "÷";

type CalcState = {
  /** Valor exibido (vírgula como decimal, estilo BR) */
  display: string;
  /** Linha auxiliar: operação pendente */
  expression: string;
  /** Operando esquerdo após escolher operador */
  accumulator: number | null;
  pendingOp: CalcOp | null;
  /** Após operador ou =, o próximo dígito substitui o display */
  replaceNext: boolean;
};

const INITIAL: CalcState = {
  display: "0",
  expression: "",
  accumulator: null,
  pendingOp: null,
  replaceNext: false,
};

function opSymbol(op: CalcOp): string {
  if (op === "−") return "−";
  if (op === "×") return "×";
  if (op === "÷") return "÷";
  return "+";
}

function parseDisplay(display: string): number {
  const normalized = display.replace(",", ".");
  const n = parseFloat(normalized);
  return Number.isFinite(n) ? n : 0;
}

function formatNum(val: number): string {
  if (!Number.isFinite(val)) return "Erro";
  if (Math.abs(val) > 1e12) return val.toExponential(6);
  let s = String(val);
  if (s.includes("e")) return s;
  if (s.includes(".")) {
    s = s.replace(/\.?0+$/, "");
    if (s.endsWith(".")) s = s.slice(0, -1);
  }
  if (s === "-0") s = "0";
  return s.replace(".", ",");
}

function applyOp(a: number, b: number, op: CalcOp): number {
  switch (op) {
    case "+":
      return a + b;
    case "−":
      return a - b;
    case "×":
      return a * b;
    case "÷":
      return b !== 0 ? a / b : NaN;
    default:
      return b;
  }
}

type CalcAction =
  | { type: "DIGIT"; digit: string }
  | { type: "DOT" }
  | { type: "OP"; op: CalcOp }
  | { type: "EQUALS" }
  | { type: "CLEAR" }
  | { type: "BACKSPACE" }
  | { type: "PERCENT" }
  | { type: "TOGGLE_SIGN" };

function calcReducer(state: CalcState, action: CalcAction): CalcState {
  switch (action.type) {
    case "CLEAR":
      return INITIAL;

    case "DIGIT": {
      const d = action.digit;
      if (!/^\d$/.test(d)) return state;
      if (state.display === "Erro") return { ...INITIAL, display: d, replaceNext: false };

      if (state.replaceNext) {
        return { ...state, display: d === "0" ? "0" : d, replaceNext: false };
      }
      if (state.display === "0") {
        return { ...state, display: d === "0" ? "0" : d };
      }
      if (state.display.replace(",", "").length >= 14) return state;
      return { ...state, display: state.display + d };
    }

    case "DOT": {
      if (state.display === "Erro") return { ...INITIAL, display: "0,", replaceNext: false };
      if (state.replaceNext) {
        return { ...state, display: "0,", replaceNext: false };
      }
      if (state.display.includes(",")) return state;
      return { ...state, display: state.display + "," };
    }

    case "BACKSPACE": {
      if (state.display === "Erro") return INITIAL;
      if (state.replaceNext) return state;
      if (state.display.length <= 1) return { ...state, display: "0" };
      const next = state.display.slice(0, -1);
      return { ...state, display: next === "" || next === "-" ? "0" : next };
    }

    case "TOGGLE_SIGN": {
      if (state.display === "Erro") return state;
      const v = parseDisplay(state.display);
      return { ...state, display: formatNum(-v) };
    }

    case "PERCENT": {
      if (state.display === "Erro") return state;
      const v = parseDisplay(state.display);
      return { ...state, display: formatNum(v / 100), replaceNext: true };
    }

    case "OP": {
      const op = action.op;
      const cur = parseDisplay(state.display);
      if (state.display === "Erro") return INITIAL;

      if (state.pendingOp !== null && !state.replaceNext && state.accumulator !== null) {
        const res = applyOp(state.accumulator, cur, state.pendingOp);
        if (!Number.isFinite(res)) {
          return { ...INITIAL, display: "Erro" };
        }
        const disp = formatNum(res);
        return {
          display: disp,
          expression: `${disp} ${opSymbol(op)}`,
          accumulator: res,
          pendingOp: op,
          replaceNext: true,
        };
      }

      return {
        ...state,
        expression: `${formatNum(cur)} ${opSymbol(op)}`,
        accumulator: cur,
        pendingOp: op,
        replaceNext: true,
      };
    }

    case "EQUALS": {
      if (state.pendingOp === null || state.accumulator === null) {
        return { ...state, replaceNext: true };
      }
      const cur = parseDisplay(state.display);
      const res = applyOp(state.accumulator, cur, state.pendingOp);
      if (!Number.isFinite(res)) {
        return { ...INITIAL, display: "Erro" };
      }
      return {
        display: formatNum(res),
        expression: "",
        accumulator: null,
        pendingOp: null,
        replaceNext: true,
      };
    }

    default:
      return state;
  }
}

function keyboardTargetIsFormField(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return !!el.closest("[contenteditable='true']");
}

function numpadDigit(code: string): string | null {
  const m = /^Numpad(\d)$/.exec(code);
  return m ? m[1] : null;
}

export default function HeaderCalculator() {
  const [open, setOpen] = React.useState(false);
  const [state, dispatch] = React.useReducer(calcReducer, INITIAL);
  const panelRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (keyboardTargetIsFormField(e.target)) return;

      const { key, code } = e;
      const np = numpadDigit(code);

      if (np !== null) {
        e.preventDefault();
        e.stopPropagation();
        dispatch({ type: "DIGIT", digit: np });
        return;
      }

      if (code === "NumpadDecimal" || code === "NumpadSeparator") {
        e.preventDefault();
        e.stopPropagation();
        dispatch({ type: "DOT" });
        return;
      }

      if (code === "NumpadAdd") {
        e.preventDefault();
        e.stopPropagation();
        dispatch({ type: "OP", op: "+" });
        return;
      }
      if (code === "NumpadSubtract") {
        e.preventDefault();
        e.stopPropagation();
        dispatch({ type: "OP", op: "−" });
        return;
      }
      if (code === "NumpadMultiply") {
        e.preventDefault();
        e.stopPropagation();
        dispatch({ type: "OP", op: "×" });
        return;
      }
      if (code === "NumpadDivide") {
        e.preventDefault();
        e.stopPropagation();
        dispatch({ type: "OP", op: "÷" });
        return;
      }
      if (code === "NumpadEnter") {
        e.preventDefault();
        e.stopPropagation();
        dispatch({ type: "EQUALS" });
        return;
      }

      if (key.length === 1 && key >= "0" && key <= "9") {
        e.preventDefault();
        e.stopPropagation();
        dispatch({ type: "DIGIT", digit: key });
        return;
      }

      if (key === "." || key === ",") {
        e.preventDefault();
        e.stopPropagation();
        dispatch({ type: "DOT" });
        return;
      }

      if (key === "+") {
        e.preventDefault();
        e.stopPropagation();
        dispatch({ type: "OP", op: "+" });
        return;
      }
      if (key === "-") {
        e.preventDefault();
        e.stopPropagation();
        dispatch({ type: "OP", op: "−" });
        return;
      }
      if (key === "*") {
        e.preventDefault();
        e.stopPropagation();
        dispatch({ type: "OP", op: "×" });
        return;
      }
      if (key === "/") {
        e.preventDefault();
        e.stopPropagation();
        dispatch({ type: "OP", op: "÷" });
        return;
      }

      if (key === "Enter" || key === "=") {
        e.preventDefault();
        e.stopPropagation();
        dispatch({ type: "EQUALS" });
        return;
      }

      if (key === "Backspace") {
        e.preventDefault();
        e.stopPropagation();
        dispatch({ type: "BACKSPACE" });
        return;
      }

      if (key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        dispatch({ type: "CLEAR" });
        return;
      }

      if (key === "%") {
        e.preventDefault();
        e.stopPropagation();
        dispatch({ type: "PERCENT" });
        return;
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [open]);

  const btn = (extra: string) =>
    `flex items-center justify-center h-11 w-full rounded-md text-[15px] font-medium transition-colors select-none active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#791614]/30 ${extra}`;

  const numBtn = btn("bg-[#f9f6f0] hover:bg-[rgba(169,171,174,0.12)] text-foreground border border-[rgba(169,171,174,0.28)] shadow-sm");
  const opBtn = btn("bg-[rgba(215,179,105,0.18)] hover:bg-[rgba(215,179,105,0.32)] text-[#791614] border border-[rgba(215,179,105,0.35)]");
  const actBtn = btn("bg-[rgba(121,22,20,0.07)] hover:bg-[rgba(121,22,20,0.14)] text-[#791614] border border-[rgba(121,22,20,0.12)]");
  const eqBtn = btn("bg-[#791614] hover:bg-[#5a0f0e] text-white border border-[#791614] shadow-sm");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0 text-muted-foreground hover:text-primary transition-colors"
          title="Calculadora (teclado ativo com painel aberto)"
        >
          <Calculator className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        ref={panelRef}
        className="w-[260px] p-3 bg-[#f9f6f0] border border-[rgba(169,171,174,0.35)] shadow-xl"
        align="end"
        onOpenAutoFocus={(ev) => ev.preventDefault()}
      >
        <div className="mb-2 rounded-md bg-[#f9f6f0] border border-[rgba(169,171,174,0.28)] px-3 py-2.5 text-right min-h-[58px] flex flex-col justify-end shadow-[inset_0_1px_2px_rgba(0,0,0,0.04)]">
          {state.expression && (
            <div className="text-[11px] text-muted-foreground truncate font-body tabular-nums">
              {state.expression}
            </div>
          )}
          <div className="text-[26px] font-semibold text-foreground font-body leading-tight truncate tabular-nums tracking-tight">
            {state.display}
          </div>
        </div>

        <p className="text-[10px] text-muted-foreground mb-2 font-body leading-snug">
          Com o painel aberto: números, + − * /, Enter ou =, vírgula ou ponto, Backspace, Esc (limpar). Teclado numérico suportado.
        </p>

        <div className="grid grid-cols-4 gap-1.5">
          <button type="button" className={actBtn} onClick={() => dispatch({ type: "CLEAR" })} title="Limpar (Esc)">
            C
          </button>
          <button type="button" className={actBtn} onClick={() => dispatch({ type: "TOGGLE_SIGN" })}>
            ±
          </button>
          <button type="button" className={actBtn} onClick={() => dispatch({ type: "PERCENT" })}>
            %
          </button>
          <button type="button" className={opBtn} onClick={() => dispatch({ type: "OP", op: "÷" })}>
            ÷
          </button>

          <button type="button" className={numBtn} onClick={() => dispatch({ type: "DIGIT", digit: "7" })}>
            7
          </button>
          <button type="button" className={numBtn} onClick={() => dispatch({ type: "DIGIT", digit: "8" })}>
            8
          </button>
          <button type="button" className={numBtn} onClick={() => dispatch({ type: "DIGIT", digit: "9" })}>
            9
          </button>
          <button type="button" className={opBtn} onClick={() => dispatch({ type: "OP", op: "×" })}>
            ×
          </button>

          <button type="button" className={numBtn} onClick={() => dispatch({ type: "DIGIT", digit: "4" })}>
            4
          </button>
          <button type="button" className={numBtn} onClick={() => dispatch({ type: "DIGIT", digit: "5" })}>
            5
          </button>
          <button type="button" className={numBtn} onClick={() => dispatch({ type: "DIGIT", digit: "6" })}>
            6
          </button>
          <button type="button" className={opBtn} onClick={() => dispatch({ type: "OP", op: "−" })}>
            −
          </button>

          <button type="button" className={numBtn} onClick={() => dispatch({ type: "DIGIT", digit: "1" })}>
            1
          </button>
          <button type="button" className={numBtn} onClick={() => dispatch({ type: "DIGIT", digit: "2" })}>
            2
          </button>
          <button type="button" className={numBtn} onClick={() => dispatch({ type: "DIGIT", digit: "3" })}>
            3
          </button>
          <button type="button" className={opBtn} onClick={() => dispatch({ type: "OP", op: "+" })}>
            +
          </button>

          <button type="button" className={`${numBtn} col-span-2`} onClick={() => dispatch({ type: "DIGIT", digit: "0" })}>
            0
          </button>
          <button type="button" className={numBtn} onClick={() => dispatch({ type: "DOT" })}>
            ,
          </button>
          <button type="button" className={eqBtn} onClick={() => dispatch({ type: "EQUALS" })}>
            =
          </button>
        </div>

        <button
          type="button"
          className={`${actBtn} w-full mt-2 text-[11px]`}
          onClick={() => dispatch({ type: "BACKSPACE" })}
          title="Apagar último dígito (Backspace)"
        >
          ⌫ Apagar
        </button>
      </PopoverContent>
    </Popover>
  );
}
